// Klientská část UNO: drží model a podle režimu zapíná zvukovou a vizuální vrstvu.
import type { GameEvent } from "../../../games/types.ts";
import type { UnoView } from "../../../games/uno/meta.ts";
import { hasAudio, hasVisual, touchReader, usesKeyboard, type Mode } from "../../store.ts";
import type { ClientGame, ClientGameModule, GameContext } from "../types.ts";
import { resultText, turnText, type Model } from "./model.ts";
import { SoundLayer } from "./sound-layer.ts";
import { VisualLayer } from "./visual-layer.ts";

class UnoClient implements ClientGame {
  private ctx: GameContext;
  private model: Model;
  private sound: SoundLayer | null = null;
  private visual: VisualLayer | null = null;

  constructor(ctx: GameContext, mode: Mode) {
    this.ctx = ctx;
    this.model = { me: ctx.me, view: null, cursor: 0, picking: null, sendAction: ctx.sendAction };
    this.setMode(mode);
  }

  setMode(mode: Mode) {
    if (hasAudio(mode) && !this.sound) this.sound = new SoundLayer(this.model, this.ctx.board, () => this.visual?.render());
    if (!hasAudio(mode) && this.sound) {
      this.sound.destroy();
      this.sound = null;
    }
    if (hasVisual(mode) && !this.visual) this.visual = new VisualLayer(this.model, this.ctx.board);
    if (!hasVisual(mode) && this.visual) {
      this.visual.destroy();
      this.visual = null;
    }
    if (this.visual) {
      this.visual.showCursor = usesKeyboard(mode);
      this.visual.setAccessible(touchReader(mode));
    }
  }

  update(view: unknown, events: GameEvent[], initial: boolean) {
    const v = view as UnoView;
    const prev = this.model.view;
    // Kurzor drží stejnou kartu, i když se ruka změní; jinak zůstane na místě.
    const cursorId = prev?.hand[this.model.cursor]?.id;
    this.model.view = v;
    const same = v.hand.findIndex((c) => c.id === cursorId);
    this.model.cursor = same >= 0 ? same : Math.max(0, Math.min(this.model.cursor, v.hand.length - 1));
    if (v.turn !== this.model.me || v.over) this.model.picking = null;
    this.renderStatus();
    if (!initial) {
      this.sound?.onEvents(events);
      this.visual?.onEvents(events);
    }
    this.visual?.render();
  }

  private renderStatus() {
    const v = this.model.view!;
    const el = this.ctx.status;
    el.textContent = "";
    const turn = document.createElement("span");
    turn.className = "status-turn" + (v.turn === this.model.me && !v.over ? " is-me" : "");
    turn.textContent = v.over ? "Konec hry" : turnText(v, this.model.me).replace(/\.$/, "");
    el.appendChild(turn);
  }

  rejected() {
    this.sound?.rejected();
  }

  resultText() {
    return this.model.view ? resultText(this.model.view, this.model.me) : "";
  }

  destroy() {
    this.sound?.destroy();
    this.visual?.destroy();
  }
}

export const unoClient: ClientGameModule = {
  id: "uno",
  boardLabel: "Hrací plocha UNO. Šipky karty v ruce, Enter zahrát, L líznout, R stav hry, H nápověda.",
  mount: (ctx, mode) => new UnoClient(ctx, mode),
};
